import api from './client';

interface HomologacaoArquivo {
  id: number;
  homologacao_id: number;
  categoria: string;
  storage_key: string;
  bucket: string;
  nome_original: string;
  mime_type: string;
  tamanho_bytes: number;
  created_at: string;
}

interface HomologacaoData {
  id: number;
  cliente_id: number | null;
  dados: Record<string, unknown>;
  status: string;
  arquivos: HomologacaoArquivo[];
  created_at: string;
  updated_at: string;
  /** card do pipe Representantes: só preenche, sem envio da procuração para assinatura */
  somentePreenchimento?: boolean;
}

interface HomologacaoListItem {
  id: number;
  cliente_id: number;
  status: string;
  ativa: number; // 1 = ativa, 0 = inativada
  potencia: string | null;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
}

export async function criarHomologacao(dados: Record<string, unknown>, clienteId?: number): Promise<{ id: number }> {
  const res = await api.post('/homologacoes', { dados, cliente_id: clienteId });
  return res.data.data;
}

export async function listarHomologacoesPorCliente(clienteId: number): Promise<HomologacaoListItem[]> {
  const res = await api.get(`/homologacoes/cliente/${clienteId}`);
  return res.data.data;
}

export async function buscarLimiteHomologacoes(clienteId: number): Promise<number> {
  try {
    const res = await api.get(`/homologacoes/cliente/${clienteId}/limite`);
    return res.data.data?.limite ?? 1;
  } catch {
    return 1;
  }
}

export async function buscarHomologacao(id: number): Promise<HomologacaoData> {
  const res = await api.get(`/homologacoes/${id}`);
  return res.data.data;
}

export async function atualizarHomologacao(id: number, dados: Record<string, unknown>, status?: string): Promise<void> {
  await api.put(`/homologacoes/${id}`, { dados, status });
}

export async function inativarHomologacao(id: number, motivo?: string): Promise<void> {
  await api.patch(`/homologacoes/${id}/inativar`, motivo ? { motivo } : {});
}

export async function uploadArquivoHomologacao(
  homologacaoId: number,
  categoria: string,
  file: File,
  onProgress?: (pct: number) => void
): Promise<{ id: number }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('categoria', categoria);

  const res = await api.post(`/homologacoes/${homologacaoId}/arquivos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return res.data.data;
}

export async function deletarArquivoHomologacao(arquivoId: number): Promise<void> {
  await api.delete(`/homologacoes/arquivos/${arquivoId}`);
}

export function obterUrlArquivoHomologacao(arquivoId: number): string {
  const apiKey = import.meta.env.VITE_API_KEY || 'Brunji1342Deguzaro@!#!@#';
  return `/api/homologacoes/arquivos/${arquivoId}/stream?key=${encodeURIComponent(apiKey)}`;
}

export function obterUrlDownloadHomologacao(arquivoId: number): string {
  const apiKey = import.meta.env.VITE_API_KEY || 'Brunji1342Deguzaro@!#!@#';
  return `/api/homologacoes/arquivos/${arquivoId}/stream?key=${encodeURIComponent(apiKey)}&download=1`;
}

export interface ContratoResumido {
  id: string;
  nomeResponsavel: string | null;
  cpfResponsavel: string | null;
  tipoCliente: string | null;
  estadoCivil: string | null;
  profissao: string | null;
  potenciaProjeto: number;
  logradouroInstalacao: string | null;
  numeroInstalacao: string | null;
  complementoInstalacao: string | null;
  bairroInstalacao: string | null;
  cidadeInstalacao: string | null;
  ufInstalacao: string | null;
  cepInstalacao: string | null;
  statusContrato: string;
  createdAt: string;
  procuradorSelecionado: 'Marcos' | 'Geraldo';
  potenciaUsada: number;
}

export async function buscarContratosPorHomologacao(homologacaoId: number): Promise<{
  contratos: ContratoResumido[];
  clienteNome: string | null;
  clienteCpf: string | null;
}> {
  const res = await api.get(`/procuracao/buscar-contrato/${homologacaoId}`);
  return { contratos: res.data.data, clienteNome: res.data.clienteNome, clienteCpf: res.data.clienteCpf };
}

export interface DadosManuaisProcuracao {
  nomeResponsavel: string;
  cpfResponsavel?: string;
  estadoCivil?: string;
  profissao?: string;
  tipoCliente?: string;
  email?: string;
  telefone?: string;
  logradouroInstalacao?: string;
  numeroInstalacao?: string;
  complementoInstalacao?: string;
  bairroInstalacao?: string;
  cidadeInstalacao?: string;
  ufInstalacao?: string;
  cepInstalacao?: string;
  // PJ
  razaoSocial?: string;
  cnpjEmpresa?: string;
  cargoResponsavel?: string;
  finalidadeEmpresa?: string;
  tipoPessoaJuridica?: string;
  // Potência (kWp) — usada apenas para selecionar o procurador em PF (<=60 → Marcos, senão Geraldo).
  potenciaProjeto?: number;
}

export async function gerarProcuracao(
  args: { contratoId: string; homologacaoId?: number } | { dadosManuais: DadosManuaisProcuracao; homologacaoId?: number } | { homologacaoId: number }
): Promise<Blob> {
  const res = await api.post('/procuracao/gerar', args, { responseType: 'blob' });
  return res.data;
}

export interface ProcuracaoAutentiqueStatus {
  enviada: boolean;
  docId?: string;
  status?: string;
  contratoId?: string;
  createdAt?: string;
  signedAt?: string;
}

export async function statusProcuracaoAutentique(homologacaoId: number): Promise<ProcuracaoAutentiqueStatus> {
  const res = await api.get(`/procuracao/status-autentique/${homologacaoId}`);
  return res.data;
}

export async function enviarProcuracaoAutentique(data: {
  contratoId?: string;
  dadosManuais?: DadosManuaisProcuracao;
  homologacaoId: number;
  signerName: string;
  signerEmail?: string;
  signerPhone?: string;
  deliveryMethod: string;
  message?: string;
  forcar?: boolean;
}) {
  const res = await api.post('/procuracao/enviar-autentique', data);
  return res.data;
}

export async function visualizarDocumentoProcuracao(homologacaoId: number): Promise<Blob> {
  const res = await api.post('/procuracao/visualizar-documento', { homologacaoId }, { responseType: 'blob' });
  return res.data;
}

export async function syncProcuracaoAutentique(homologacaoId: number) {
  const res = await api.post('/procuracao/sync-autentique', { homologacaoId });
  return res.data;
}

export async function cancelarProcuracaoAutentique(homologacaoId: number) {
  const res = await api.post('/procuracao/cancelar-autentique', { homologacaoId });
  return res.data;
}

export async function uploadProcuracaoAssinada(args: {
  file: File;
  homologacaoId: number;
  signerName: string;
  signerEmail?: string;
  signerPhone?: string;
  contratoId?: string;
  dadosManuais?: DadosManuaisProcuracao;
  forcar?: boolean;
  onProgress?: (pct: number) => void;
}): Promise<{ success: boolean; arquivoId: number; status: string; signedUrl: string }> {
  const formData = new FormData();
  formData.append('file', args.file);
  formData.append('homologacaoId', String(args.homologacaoId));
  formData.append('signerName', args.signerName);
  if (args.signerEmail) formData.append('signerEmail', args.signerEmail);
  if (args.signerPhone) formData.append('signerPhone', args.signerPhone);
  if (args.contratoId) formData.append('contratoId', args.contratoId);
  if (args.dadosManuais) formData.append('dadosManuais', JSON.stringify(args.dadosManuais));
  if (args.forcar) formData.append('forcar', 'true');
  const res = await api.post('/procuracao/upload-assinada', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (e.total && args.onProgress) args.onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return res.data;
}

export async function registrarProcuracaoAvulsa(
  dadosManuais: DadosManuaisProcuracao,
  homologacaoId?: number
): Promise<{ id: number }> {
  const res = await api.post('/procuracao/registrar-avulsa', { dadosManuais, homologacaoId });
  return { id: res.data.id };
}

export interface ProcuracaoAvulsa {
  id: number;
  nome: string | null;
  tipo: 'PF' | 'PJ' | null;
  status: string | null; // null = só gerada (não enviada)
  doc_id: string | null;
  enviado_em: string | null;
  assinado_em: string | null;
  signed_url: string | null;
  signer_name: string | null;
  signer_email: string | null;
  signer_phone: string | null;
  delivery_method: string | null;
  created_at: string;
  updated_at: string;
}

export async function listarProcuracoesAvulsas(): Promise<ProcuracaoAvulsa[]> {
  const res = await api.get('/procuracao/avulsas');
  return res.data.data;
}

export interface HistoricoItem {
  id: number;
  homologacao_id: number;
  acao: string;
  detalhes: Record<string, unknown> | null;
  usuario: string | null;
  created_at: string;
}

export async function listarHistorico(homologacaoId: number): Promise<HistoricoItem[]> {
  const res = await api.get(`/homologacoes/${homologacaoId}/historico`);
  return res.data.data;
}

export type { HomologacaoArquivo, HomologacaoData };
